import React, { useEffect, useRef, useState } from 'react';
import { X, Download, File, Loader } from 'react-feather';
import heic2any from 'heic2any';

const dataUrlToBlob = (dataUrl) => {
	const [meta = '', base64 = ''] = dataUrl.split(',');
	const mimeMatch = meta.match(/data:(.*?);base64/);
	const mimeType = mimeMatch?.[1] || 'application/octet-stream';
	const binary = window.atob(base64);
	const bytes = new Uint8Array(binary.length);

	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}

	return new Blob([bytes], { type: mimeType });
};

const FileViewerModal = ({
	isOpen,
	onClose,
	fileUrl,
	fileName,
	fileType,
	footerContent = null,
}) => {
	const [displayUrl, setDisplayUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [loadingMessage, setLoadingMessage] = useState('Loading preview...');
	const objectUrlRef = useRef(null);

	useEffect(() => {
		const cleanupObjectUrl = () => {
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};

		if (isOpen && fileUrl) {
            // Reset state
            setDisplayUrl(null);
            setError(null);
			setLoadingMessage('Loading preview...');
			cleanupObjectUrl();

			const isHeic = 
                fileType?.toLowerCase() === 'image/heic' || 
                fileType?.toLowerCase() === 'image/heif' || 
                fileName?.toLowerCase().endsWith('.heic') || 
                fileName?.toLowerCase().endsWith('.heif') ||
                fileUrl.toLowerCase().includes('.heic');
			const isPdfDataUri =
				fileType?.toLowerCase() === 'application/pdf' &&
				fileUrl.startsWith('data:application/pdf');

			if (isPdfDataUri) {
				setLoading(true);
				setLoadingMessage('Loading PDF preview...');

				try {
					const blob = dataUrlToBlob(fileUrl);
					const url = URL.createObjectURL(blob);
					objectUrlRef.current = url;
					setDisplayUrl(url);
				} catch (err) {
					console.error('PDF preview preparation failed:', err);
					setError('Could not preview PDF file.');
					setDisplayUrl(fileUrl);
				} finally {
					setLoading(false);
				}
			} else if (isHeic) {
				setLoading(true);
				setLoadingMessage('Converting image for preview...');
				fetch(fileUrl)
					.then((res) => res.blob())
					.then((blob) => heic2any({ blob, toType: 'image/jpeg' }))
					.then((conversionResult) => {
                        // conversionResult can be a blob or array of blobs. We take the first if array.
                        const blob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
						const url = URL.createObjectURL(blob);
						objectUrlRef.current = url;
						setDisplayUrl(url);
						setLoading(false);
					})
					.catch((err) => {
						console.error('HEIC conversion failed:', err);
						setError('Could not preview HEIC file.');
						setLoading(false);
                        setDisplayUrl(fileUrl); // Fallback to original
					});
			} else {
                setLoading(false);
				setDisplayUrl(fileUrl);
			}
		}
        
        // Cleanup object URLs to avoid memory leaks
        return () => {
			cleanupObjectUrl();
        };

	}, [isOpen, fileUrl, fileType, fileName]);

	if (!isOpen) return null;

	// Improved detection for display logic
	const isImage = 
        !error && 
        !loading && 
        (
            fileType?.startsWith('image/') || 
            fileUrl?.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i) ||
            fileName?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ||
            (
				displayUrl?.startsWith('blob:') &&
				fileType?.startsWith('image/')
			) // HEIC/image blob previews
        );
        
	const isPdf = fileType === 'application/pdf' || fileUrl?.match(/\.pdf(\?.*)?$/i) || fileName?.endsWith('.pdf');

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose}>
			<div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col relative" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="flex justify-between items-center p-4 border-b border-gray-200">
					<h3 className="text-lg font-semibold text-gray-800 truncate pr-4" title={fileName}>
						{fileName || 'Attachment'}
					</h3>
					<div className="flex items-center gap-2">
						<a
							href={fileUrl} // Always download original
							download={fileName || 'download'}
							target="_blank"
							rel="noopener noreferrer"
							className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors"
							title="Download Original"
						>
							<Download size={20} />
						</a>
						<button
							onClick={onClose}
							className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-full transition-colors"
							title="Close"
						>
							<X size={24} />
						</button>
					</div>
				</div>

				{/* Content */}
				<div className="flex-1 overflow-hidden bg-gray-100 flex items-center justify-center p-4 relative">
					{loading ? (
						<div className="text-center">
                            <Loader className="animate-spin text-blue-600 mb-2 mx-auto" size={48} />
                            <p className="text-gray-600">{loadingMessage}</p>
                        </div>
					) : isImage ? (
						<img
							src={displayUrl || fileUrl}
							alt={fileName}
							className="max-w-full max-h-full object-contain shadow-sm"
						/>
					) : isPdf ? (
						<iframe
							src={displayUrl || fileUrl}
							title={fileName}
							className="w-full h-full border-none shadow-sm bg-white"
						/>
					) : (
						<div className="text-center p-8">
							<div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-500">
								<File size={40} />
							</div>
							<p className="text-lg text-gray-700 mb-2">
                                {error ? error : "Detailed preview not available for this file type."}
                            </p>
							<p className="text-sm text-gray-500 mb-6">{fileName}</p>
							<a
								href={fileUrl}
								download={fileName || 'download'}
                                target="_blank"
								rel="noopener noreferrer"
								className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
							>
								<Download size={18} />
								Download File
							</a>
						</div>
					)}
				</div>
				{footerContent && (
					<div className="border-t border-gray-200 bg-white p-4">
						{footerContent}
					</div>
				)}
			</div>
		</div>
	);
};

export default FileViewerModal;
